import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { SignInPanel } from '../components/auth/SignInPanel'
import { useSession } from '../hooks/useSession'
import { relativeTime } from '../lib/relativeTime'
import { wsStore, loadWorkspace, resetWorkspace, type Grid } from '../lib/workspaceStore'
import { EditablePill } from '../components/EditablePill'

function formatBytes(b: number): string {
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(1)} GB`
  if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(1)} MB`
  return `${Math.round(b / 1024)} KB`
}

type FilterTag = 'all' | 'public' | 'private'
type SortKey = 'recent' | 'rows' | 'name'
type RowStyle = 'A' | 'B' | 'C'

const GRID_TABLE_COLS = '2fr 0.7fr 0.6fr 0.7fr 0.8fr 0.7fr'

async function patchGridField<K extends 'visibility' | 'editable'>(g: Grid, field: K, value: Grid[K]) {
  const r = await fetch(`/api/grids/${g.slug}/${field}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ [field]: value }),
  })
  if (!r.ok) return
  const cur = wsStore.get()
  if (!cur.workspace) return
  const grids = cur.workspace.grids.map(x =>
    x.slug === g.slug && x.handle === g.handle ? { ...x, [field]: value } : x
  )
  wsStore.set({ ...cur, workspace: { ...cur.workspace, grids } })
}

export default function ProjectList() {
  const navigate = useNavigate()
  const { status } = useSession()
  const ws = wsStore.use()
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [filter, setFilter] = useState<FilterTag>('all')
  const [sort, setSort] = useState<SortKey>('recent')
  const [search, setSearch] = useState('')
  const [toggling, setToggling] = useState<Set<string>>(new Set())
  const [togglingEditable, setTogglingEditable] = useState<Set<string>>(new Set())
  const [pendingDelete, setPendingDelete] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<Set<string>>(new Set())
  const [rowStyle, setRowStyle] = useState<RowStyle>('C')
  const [hoveredRow, setHoveredRow] = useState<string | null>(null)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)

  useEffect(() => {
    if (!openDropdown) return
    const close = () => { setOpenDropdown(null); setPendingDelete(null) }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [openDropdown])

  const workspace = ws.workspace
  const keyCount = ws.keyCount
  const loading = ws.status === 'loading'
  const error = ws.error
  const plan = ws.plan ?? 'free'
  const storageBytes = ws.storageBytes
  const storageLimitBytes = ws.storageLimitBytes
  const [storageDismissed, setStorageDismissed] = useState(false)

  const showStorageWarning =
    !storageDismissed &&
    storageBytes !== null &&
    storageLimitBytes !== null &&
    storageLimitBytes > 0 &&
    storageBytes / storageLimitBytes >= 0.8

  useEffect(() => {
    if (status === 'signed-in') {
      loadWorkspace()
    }
    if (status === 'signed-out') {
      resetWorkspace()
    }
  }, [status])

  // ── Auth gate ─────────────────────────────────────────────────────────
  if (status === 'probing') {
    return (
      <div className="container" style={{ padding: '120px 0', textAlign: 'center' }}>
        <p className="muted mono" style={{ fontSize: 13 }}>Checking session…</p>
      </div>
    )
  }

  if (status === 'signed-out' && sentTo) {
    return (
      <div style={{ padding: '80px 0' }}>
        <div className="container" style={{ maxWidth: 480 }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}><span className="num">~</span>workspace</div>
          <h1 className="h1">Check your email.</h1>
          <p className="lede" style={{ marginTop: 14 }}>
            We sent a sign-in link to <strong style={{ color: 'var(--fg)' }}>{sentTo}</strong>. The link expires in 5 minutes.
          </p>
          <p className="muted-2" style={{ fontSize: 12, marginTop: 24 }}>
            Didn't arrive? Check spam, or{' '}
            <span onClick={() => setSentTo(null)} style={{ color: 'var(--accent)', cursor: 'pointer' }}>
              try again →
            </span>
          </p>
        </div>
      </div>
    )
  }

  if (status === 'signed-out') {
    return (
      <div style={{ padding: '80px 0' }}>
        <div className="container" style={{ maxWidth: 480 }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}><span className="num">~</span>workspace</div>
          <h1 className="h1">Sign in.</h1>
          <p className="lede" style={{ marginTop: 14, marginBottom: 28 }}>
            Continue with GitHub, or get a one-click sign-in link by email.
          </p>
          <SignInPanel callbackURL="/workspace" onMagicLinkSent={setSentTo} />
          <p className="muted-2" style={{ fontSize: 12, marginTop: 16 }}>
            New here?{' '}
            <span onClick={() => navigate('/docs/quickstart')} style={{ color: 'var(--accent)', cursor: 'pointer' }}>
              Read the quickstart →
            </span>
          </p>
        </div>
      </div>
    )
  }

  // ── Handle claim gate (signed in but no handle yet) ──────────────────
  if (ws.status === 'ready' && workspace && !workspace.handle) {
    return <HandleClaim onClaimed={loadWorkspace} />
  }

  // ── Workspace view (signed in) ───────────────────────────────────────
  const grids = workspace?.grids ?? []
  const filtered = grids
    .filter(g => filter === 'all' || g.visibility === filter)
    .filter(g => !search || g.slug.toLowerCase().includes(search.toLowerCase()) || g.handle.toLowerCase().includes(search.toLowerCase()))
  const sorted = [...filtered].sort((a, b) => {
    if (sort === 'rows') return b.rows - a.rows
    if (sort === 'name') return a.slug.localeCompare(b.slug)
    return b.updated_at.localeCompare(a.updated_at)
  })
  const totalRows = grids.reduce((acc, g) => acc + g.rows, 0)
  const totalVer = grids.reduce((acc, g) => acc + g.version, 0)
  const publicCount = grids.filter(g => g.visibility === 'public').length
  const privateCount = grids.filter(g => g.visibility === 'private').length

  async function toggleVisibility(g: Grid) {
    setToggling(s => new Set(s).add(g.slug))
    try { await patchGridField(g, 'visibility', g.visibility === 'public' ? 'private' : 'public') }
    finally { setToggling(s => { const n = new Set(s); n.delete(g.slug); return n }) }
  }

  async function toggleEditable(g: Grid) {
    setTogglingEditable(s => new Set(s).add(g.slug))
    try { await patchGridField(g, 'editable', g.editable === false) }
    finally { setTogglingEditable(s => { const n = new Set(s); n.delete(g.slug); return n }) }
  }

  async function confirmDelete(slug: string) {
    const handle = workspace?.handle ?? ''
    setDeleting(s => new Set(s).add(slug))
    setPendingDelete(null)
    try {
      const r = await fetch(`/api/grids/${slug}`, { method: 'DELETE', credentials: 'include' })
      if (r.ok) {
        const cur = wsStore.get()
        if (cur.workspace) {
          const grids = cur.workspace.grids.filter(x => !(x.slug === slug && x.handle === handle))
          wsStore.set({ ...cur, workspace: { ...cur.workspace, grids } })
        }
      }
    } finally {
      setDeleting(s => { const n = new Set(s); n.delete(slug); return n })
    }
  }

  return (
    <div style={{ padding: '40px 0 80px' }}>
      <div className="container-wide">
        <div style={{ marginBottom: 28 }}>
          <h1 className="h1">Grids</h1>
          <p className="lede" style={{ marginTop: 8, fontSize: 14, color: 'var(--fg-2)' }}>
            View and manage every grid pushed to your workspace.
          </p>
          <div className="mono" style={{ marginTop: 14, fontSize: 12, color: 'var(--fg-3)' }}>
            <span className="num">~</span>{workspace?.handle ?? '…'}
            <span style={{ margin: '0 8px' }}>·</span>
            <span>{plan} plan</span>
          </div>
        </div>

        {showStorageWarning && storageBytes !== null && storageLimitBytes !== null && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            padding: '10px 16px', marginBottom: 20,
            background: 'color-mix(in srgb, var(--amber, #f59e0b) 10%, transparent)',
            border: '1px solid color-mix(in srgb, var(--amber, #f59e0b) 30%, transparent)',
            borderRadius: 6,
          }}>
            <span className="mono" style={{ fontSize: 12, color: 'var(--fg-1)' }}>
              Storage: {formatBytes(storageBytes)} of {formatBytes(storageLimitBytes)} used
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={() => navigate('/pricing')}
                className="mono"
                style={{ fontSize: 12, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                Upgrade →
              </button>
              <button
                onClick={() => setStorageDismissed(true)}
                className="mono"
                style={{ fontSize: 12, color: 'var(--fg-3)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                ✕
              </button>
            </div>
          </div>
        )}

        <div className="resp-grid-4" style={{ gap: 1, background: 'var(--line-soft)', border: '1px solid var(--line-soft)', borderRadius: 8, overflow: 'hidden', marginBottom: 28 }}>
          {[
            { label: 'grids',      value: grids.length.toString(),                                                                  sub: 'in your workspace' },
            { label: 'rows',       value: totalRows >= 1e6 ? (totalRows / 1e6).toFixed(2) + 'M' : totalRows.toLocaleString(),       sub: 'across all grids' },
            { label: 'snapshots',  value: totalVer.toLocaleString(),                                                                sub: 'time machine' },
            { label: 'visibility', value: `${publicCount} public · ${privateCount} private`,                                        sub: grids.length === 0 ? '—' : `${publicCount} of ${grids.length} public` },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--bg-1)', padding: 18 }}>
              <div className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>{s.label}</div>
              <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--fg)' }}>{s.value}</div>
              <div className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 4 }}>{s.sub}</div>
            </div>
          ))}
        </div>

        <div className="row" style={{ marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
          <div className="row gap-4" style={{ background: 'var(--bg-1)', border: '1px solid var(--line-soft)', borderRadius: 6, padding: 4 }}>
            {(['all', 'public', 'private'] as FilterTag[]).map(f => (
              <button key={f} onClick={() => setFilter(f)} className="mono"
                style={{
                  padding: '5px 11px', fontSize: 12, borderRadius: 4,
                  background: filter === f ? 'var(--bg-3)' : 'transparent',
                  color: filter === f ? 'var(--fg)' : 'var(--fg-2)',
                  border: 'none', cursor: 'pointer',
                }}>{f}</button>
            ))}
          </div>
          <div style={{ flex: 1, minWidth: 240, position: 'relative' }}>
            <span className="mono" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--fg-3)', fontSize: 12 }}>⌕</span>
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="search grids by name or handle…"
              className="mono"
              style={{
                width: '100%', padding: '8px 12px 8px 28px', fontSize: 12,
                background: 'var(--bg-1)', border: '1px solid var(--line-soft)',
                borderRadius: 6, color: 'var(--fg)', outline: 'none',
              }}
            />
          </div>
          <select value={sort} onChange={e => setSort(e.target.value as SortKey)} className="mono"
            style={{ padding: '8px 12px', fontSize: 12, background: 'var(--bg-1)', border: '1px solid var(--line-soft)', borderRadius: 6, color: 'var(--fg-1)' }}>
            <option value="recent">recent</option>
            <option value="rows">most rows</option>
            <option value="name">name a–z</option>
          </select>
        </div>

        {loading && <div className="muted" style={{ padding: '40px 0', fontSize: 13 }}>Loading…</div>}
        {error && (
          <div style={{ padding: '12px 16px', background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', borderRadius: 6, color: 'var(--err)', fontSize: 13, marginBottom: 16 }}>
            {error}
          </div>
        )}

        {!loading && grids.length === 0 && !error && keyCount === 0 && (
          <div style={{ textAlign: 'center', padding: '64px 24px' }}>
            <button className="btn btn-primary btn-mono" onClick={() => navigate('/get-started')}>
              Generate API key →
            </button>
          </div>
        )}
        {!loading && grids.length === 0 && !error && keyCount !== null && keyCount > 0 && (
          <div className="card card-pad" style={{ textAlign: 'center', padding: '48px 24px' }}>
            <div className="h3" style={{ marginBottom: 8 }}>Push your first grid.</div>
            <p className="muted" style={{ fontSize: 13, marginBottom: 22 }}>
              Pipe JSON, JSONL, or CSV into <code className="mono">instadash push</code> from your terminal.
            </p>
            <pre className="mono" style={{ display: 'inline-block', textAlign: 'left', padding: '14px 18px', background: 'var(--bg)', border: '1px solid var(--line-soft)', borderRadius: 6, fontSize: 12, lineHeight: 1.7, color: 'var(--lime)', margin: 0 }}>
{`# from a file
npx instadash push --name sales --file data.jsonl

# from stdin
echo '{"hello":"world"}' | npx instadash push --name first`}
            </pre>
            <p className="muted-2" style={{ fontSize: 12, marginTop: 18 }}>
              Need to install? <code className="mono">npm i -g instadash</code> · run <code className="mono">instadash</code> with no args for full help.
            </p>
          </div>
        )}

        {!loading && sorted.length > 0 && (
          <>
            {/* A/B test: rowStyle controls the action pattern per row.
                'C' (kebab) is live. 'A' (pill toggle) and 'B' (hover reveal) are built
                and ready — set rowStyle default to swap. */}

            <div className="card" style={{ overflow: rowStyle === 'C' ? 'visible' : 'hidden', overflowX: rowStyle === 'C' ? 'visible' : 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: GRID_TABLE_COLS, padding: '10px 18px', borderBottom: '1px solid var(--line-soft)', background: 'var(--bg-2)', minWidth: 640 }}>
                {['grid', 'rows', 'version', 'visibility', 'updated', ''].map(h => (
                  <div key={h} className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</div>
                ))}
              </div>
              {sorted.map(g => {
                const rowKey = `${g.handle}/${g.slug}`
                const isHovered = hoveredRow === rowKey
                const isDropOpen = openDropdown === rowKey

                return (
                  <div key={rowKey}
                    onClick={() => { setOpenDropdown(null); navigate(`/${g.handle}/${g.slug}`) }}
                    onMouseEnter={() => setHoveredRow(rowKey)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={{
                      display: 'grid', gridTemplateColumns: GRID_TABLE_COLS,
                      padding: '14px 18px', borderBottom: '1px solid var(--line-soft)',
                      cursor: 'pointer', alignItems: 'center', transition: 'background .12s',
                      minWidth: 640,
                      background: isHovered ? 'var(--bg-2)' : 'transparent',
                      position: 'relative',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 500 }}>{g.slug}</span>
                        <EditablePill editable={g.editable} />
                      </div>
                      <div className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 2 }}>/{g.handle}/{g.slug}</div>
                    </div>

                    <div className="mono" style={{ fontSize: 13, color: 'var(--fg-1)' }}>{g.rows.toLocaleString()}</div>

                    <div className="mono" style={{ fontSize: 13, color: 'var(--fg-2)' }}>v{g.version}</div>

                    <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {rowStyle === 'A' ? (
                        <button
                          onClick={() => { if (!toggling.has(g.slug)) toggleVisibility(g) }}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5,
                            padding: '4px 10px', borderRadius: 20, cursor: 'pointer',
                            background: g.visibility === 'public' ? 'rgba(184,255,58,0.1)' : 'var(--bg-2)',
                            border: `1px solid ${g.visibility === 'public' ? 'rgba(184,255,58,0.35)' : 'var(--line-soft)'}`,
                            color: g.visibility === 'public' ? 'var(--lime)' : 'var(--fg-3)',
                            fontSize: 11, opacity: toggling.has(g.slug) ? 0.5 : 1,
                            transition: 'all .15s',
                          }}>
                          <span style={{ fontSize: 10 }}>{g.visibility === 'public' ? '🌐' : '🔒'}</span>
                          <span className="mono">{toggling.has(g.slug) ? '…' : g.visibility}</span>
                        </button>
                      ) : rowStyle === 'B' ? (
                        isHovered ? (
                          <button
                            onClick={() => { if (!toggling.has(g.slug)) toggleVisibility(g) }}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                              background: 'var(--bg-3)', border: '1px solid var(--line-soft)',
                              color: 'var(--fg-2)', fontSize: 11,
                              opacity: toggling.has(g.slug) ? 0.5 : 1,
                            }}>
                            <span className="mono" style={{ color: 'var(--fg-4)', fontSize: 10 }}>↕</span>
                            <span className="mono">{toggling.has(g.slug) ? '…' : `→ ${g.visibility === 'public' ? 'private' : 'public'}`}</span>
                          </button>
                        ) : (
                          <span className={`chip ${g.visibility === 'public' ? 'chip-lime' : ''}`} style={{ fontSize: 10 }}>
                            <span className="dot"></span>{g.visibility}
                          </span>
                        )
                      ) : (
                        /* C: static chip, actions in dropdown */
                        <span className={`chip ${g.visibility === 'public' ? 'chip-lime' : ''}`} style={{ fontSize: 10 }}>
                          <span className="dot"></span>{g.visibility}
                        </span>
                      )}
                    </div>

                    <div>
                      <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>{relativeTime(g.updated_at)}</span>
                      {g.expires_at && (
                        <div className="mono" style={{ fontSize: 10, color: 'var(--amber, #f59e0b)', marginTop: 2 }}>
                          ⏱ {relativeTime(g.expires_at)}
                        </div>
                      )}
                    </div>

                    <div style={{ textAlign: 'right', position: 'relative' }} onClick={e => e.stopPropagation()}>

                      {rowStyle === 'A' && (
                        deleting.has(g.slug) ? (
                          <span className="mono" style={{ fontSize: 11, color: 'var(--fg-4)' }}>deleting…</span>
                        ) : pendingDelete === g.slug ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>delete?</span>
                            <button onClick={() => confirmDelete(g.slug)}
                              style={{ padding: '3px 10px', borderRadius: 4, fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer', background: 'rgba(255,100,100,0.12)', border: '1px solid rgba(255,100,100,0.35)', color: 'var(--err, #ff6b6b)' }}>
                              yes
                            </button>
                            <button onClick={() => setPendingDelete(null)}
                              style={{ padding: '3px 9px', borderRadius: 4, fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer', background: 'var(--bg-2)', border: '1px solid var(--line-soft)', color: 'var(--fg-3)' }}>
                              no
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => setPendingDelete(g.slug)}
                            style={{ padding: '4px 8px', borderRadius: 4, fontSize: 14, background: 'transparent', border: 'none', color: isHovered ? 'var(--fg-3)' : 'transparent', cursor: 'pointer', transition: 'color .12s' }}
                            title="Delete grid">
                            ⌦
                          </button>
                        )
                      )}

                      {rowStyle === 'B' && (
                        deleting.has(g.slug) ? (
                          <span className="mono" style={{ fontSize: 11, color: 'var(--fg-4)' }}>deleting…</span>
                        ) : pendingDelete === g.slug ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>delete?</span>
                            <button onClick={() => confirmDelete(g.slug)}
                              style={{ padding: '3px 10px', borderRadius: 4, fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer', background: 'rgba(255,100,100,0.12)', border: '1px solid rgba(255,100,100,0.35)', color: 'var(--err, #ff6b6b)' }}>
                              yes
                            </button>
                            <button onClick={() => setPendingDelete(null)}
                              style={{ padding: '3px 9px', borderRadius: 4, fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer', background: 'var(--bg-2)', border: '1px solid var(--line-soft)', color: 'var(--fg-3)' }}>
                              no
                            </button>
                          </div>
                        ) : isHovered ? (
                          <button onClick={() => setPendingDelete(g.slug)}
                            style={{ padding: '4px 10px', borderRadius: 4, fontSize: 11, fontFamily: 'var(--mono)', background: 'var(--bg-3)', border: '1px solid var(--line-soft)', color: 'var(--fg-3)', cursor: 'pointer' }}>
                            delete
                          </button>
                        ) : null
                      )}

                      {rowStyle === 'C' && (
                        deleting.has(g.slug) ? (
                          <span className="mono" style={{ fontSize: 11, color: 'var(--fg-4)' }}>deleting…</span>
                        ) : (
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <button
                              onClick={() => setOpenDropdown(isDropOpen ? null : rowKey)}
                              style={{
                                padding: '4px 10px', borderRadius: 4, fontSize: 14,
                                background: isDropOpen ? 'var(--bg-3)' : 'transparent',
                                border: `1px solid ${isDropOpen ? 'var(--line-soft)' : 'transparent'}`,
                                color: isHovered || isDropOpen ? 'var(--fg-2)' : 'transparent',
                                cursor: 'pointer', transition: 'color .12s',
                                letterSpacing: 1,
                              }}>
                              ···
                            </button>
                            {isDropOpen && (
                              <div
                                onMouseDown={e => e.stopPropagation()}
                                style={{
                                position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 50,
                                background: 'var(--bg-1)', border: '1px solid var(--line-soft)',
                                borderRadius: 8, padding: '4px 0', minWidth: 170,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                              }}>
                                {toggling.has(g.slug) ? (
                                  <div className="mono" style={{ padding: '9px 14px', fontSize: 12, color: 'var(--fg-4)' }}>updating…</div>
                                ) : (
                                  <button
                                    className="dropdown-item"
                                    onClick={() => { toggleVisibility(g); setOpenDropdown(null) }}
                                    style={{ color: 'var(--fg-2)' }}>
                                    <span>{g.visibility === 'public' ? '🔒' : '🌐'}</span>
                                    Make {g.visibility === 'public' ? 'private' : 'public'}
                                  </button>
                                )}
                                {togglingEditable.has(g.slug) ? (
                                  <div className="mono" style={{ padding: '9px 14px', fontSize: 12, color: 'var(--fg-4)' }}>updating…</div>
                                ) : (
                                  <button
                                    className="dropdown-item"
                                    onClick={() => { toggleEditable(g); setOpenDropdown(null) }}
                                    style={{ color: 'var(--fg-2)' }}>
                                    <span>{g.editable === false ? '✎' : '🛡'}</span>
                                    Make {g.editable === false ? 'editable' : 'locked'}
                                  </button>
                                )}
                                <div style={{ height: 1, background: 'var(--line-soft)', margin: '4px 0' }} />
                                {pendingDelete === g.slug ? (
                                  <div style={{ padding: '6px 14px' }}>
                                    <div className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', marginBottom: 6 }}>confirm delete?</div>
                                    <div style={{ display: 'flex', gap: 6 }}>
                                      <button onClick={() => confirmDelete(g.slug)}
                                        style={{ flex: 1, padding: '5px 0', borderRadius: 4, fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer', background: 'rgba(255,100,100,0.15)', border: '1px solid rgba(255,100,100,0.35)', color: 'var(--err, #ff6b6b)' }}>
                                        delete
                                      </button>
                                      <button onClick={() => setPendingDelete(null)}
                                        style={{ flex: 1, padding: '5px 0', borderRadius: 4, fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer', background: 'var(--bg-2)', border: '1px solid var(--line-soft)', color: 'var(--fg-3)' }}>
                                        cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <button
                                    className="dropdown-item"
                                    onClick={() => setPendingDelete(g.slug)}
                                    style={{ color: 'var(--err, #ff6b6b)' }}>
                                    <span>⌦</span> Delete grid
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ── Handle claim form ────────────────────────────────────────────────────────

type CheckStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid' | 'error'

function HandleClaim({ onClaimed }: { onClaimed: () => void }) {
  const [value, setValue] = useState('')
  const [check, setCheck] = useState<CheckStatus>('idle')
  const [reason, setReason] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    if (!value) { setCheck('idle'); setReason(null); return }
    setCheck('checking')
    setReason(null)
    const ctrl = new AbortController()
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`/api/handle/check?h=${encodeURIComponent(value)}`, {
          credentials: 'include',
          signal: ctrl.signal,
        })
        const d = await r.json() as { available?: boolean; valid?: boolean; reason?: string | null }
        if (!d.valid) { setCheck('invalid'); setReason(d.reason ?? 'Invalid handle.'); return }
        if (!d.available) { setCheck('taken'); setReason(d.reason ?? 'Taken.'); return }
        setCheck('available'); setReason(null)
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return
        setCheck('error'); setReason('Check failed.')
      }
    }, 250)
    return () => { clearTimeout(t); ctrl.abort() }
  }, [value])

  async function submit() {
    if (check !== 'available' || submitting) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      const r = await fetch('/api/handle', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handle: value }),
      })
      if (!r.ok) {
        const d = await r.json().catch(() => ({})) as { error?: string }
        setSubmitError(d.error ?? `Failed (HTTP ${r.status})`)
        setSubmitting(false)
        return
      }
      onClaimed()
    } catch {
      setSubmitError('Network error. Try again.')
      setSubmitting(false)
    }
  }

  const feedbackColor =
    check === 'available' ? 'var(--lime)' :
    check === 'idle' || check === 'checking' ? 'var(--fg-3)' : 'var(--err)'

  const feedback =
    submitError ? submitError :
    check === 'idle' ? '3–20 characters. Lowercase letters, numbers, hyphens.' :
    check === 'checking' ? 'Checking…' :
    check === 'available' ? '✓ available' :
    reason ?? ''

  const canSubmit = check === 'available' && !submitting

  return (
    <div style={{ padding: '80px 0' }}>
      <div className="container" style={{ maxWidth: 560 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}><span className="num">~</span>workspace</div>
        <h1 className="h1">Pick your handle.</h1>
        <p className="lede" style={{ marginTop: 14, marginBottom: 28 }}>
          Your handle becomes part of every grid URL you share.
          This is permanent — choose carefully.
        </p>

        <input
          autoFocus
          className="mono"
          value={value}
          onChange={e => setValue(e.target.value.toLowerCase().slice(0, 20))}
          onKeyDown={e => { if (e.key === 'Enter') submit() }}
          placeholder="raihan"
          style={{
            width: '100%',
            padding: '12px 14px',
            background: 'var(--bg-1)',
            border: '1px solid var(--line-soft)',
            borderRadius: 6,
            color: 'var(--fg)',
            fontSize: 14,
            outline: 'none',
          }}
        />

        <div className="mono" style={{ fontSize: 12, marginTop: 10, minHeight: 18, color: feedbackColor }}>
          {feedback}
        </div>

        <div className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 14, lineHeight: 1.7 }}>
          your grids: instadash.io/<span style={{ color: value ? 'var(--accent)' : 'var(--fg-3)' }}>{value || '<handle>'}</span>/grid-name
        </div>

        <button
          className="btn btn-primary btn-mono"
          disabled={!canSubmit}
          onClick={submit}
          style={{ marginTop: 22, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
        >
          {submitting ? 'Claiming…' : 'Claim handle'}
        </button>
      </div>
    </div>
  )
}
